import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import api from '../services/api';

export default function Reports() {
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { data: performance, isLoading: loadingPerformance } = useQuery({
    queryKey: ['performance-report', startDate, endDate],
    queryFn: async () => {
      const response = await api.get(
        `/reports/performance?startDate=${startDate}&endDate=${endDate}`
      );
      return response.data.data;
    },
  });

  const { data: revenue } = useQuery({
    queryKey: ['revenue-report', startDate, endDate],
    queryFn: async () => {
      const response = await api.get(
        `/reports/revenue?startDate=${startDate}&endDate=${endDate}`
      );
      return response.data.data;
    },
  });

  const { data: trafficSources, isLoading: loadingTraffic } = useQuery({
    queryKey: ['traffic-report', startDate, endDate],
    queryFn: async () => {
      const response = await api.get(
        `/reports/traffic-sources?startDate=${startDate}&endDate=${endDate}`
      );
      return response.data.data;
    },
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Reports</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Analytics and performance reports.
          </p>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="mt-6 flex gap-4 items-center">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-[var(--text-secondary)]">
            Start Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 input"
          />
        </div>
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-[var(--text-secondary)]">
            End Date
          </label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 input"
          />
        </div>
      </div>

      {/* Revenue Summary */}
      {revenue?.totals && (
        <div className="mt-8">
          <h2 className="text-lg font-medium text-[var(--text-primary)]">Revenue Summary</h2>
          <dl className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-[var(--bg-secondary)] overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-[var(--text-muted)] truncate">Total Deposits</dt>
              <dd className="mt-1 text-3xl font-semibold text-[var(--text-primary)]">
                ${revenue.totals.totalDeposits?.toLocaleString() || 0}
              </dd>
            </div>
            <div className="bg-[var(--bg-secondary)] overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-[var(--text-muted)] truncate">Total Bets</dt>
              <dd className="mt-1 text-3xl font-semibold text-[var(--text-primary)]">
                ${revenue.totals.totalBets?.toLocaleString() || 0}
              </dd>
            </div>
            <div className="bg-[var(--bg-secondary)] overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-[var(--text-muted)] truncate">GGR</dt>
              <dd className="mt-1 text-3xl font-semibold text-green-600">
                ${revenue.totals.ggr?.toLocaleString() || 0}
              </dd>
            </div>
            <div className="bg-[var(--bg-secondary)] overflow-hidden shadow rounded-lg px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-[var(--text-muted)] truncate">Total Lots</dt>
              <dd className="mt-1 text-3xl font-semibold text-[var(--text-primary)]">
                {revenue.totals.totalLots?.toFixed(2) || 0}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {/* Top Affiliates */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-[var(--text-primary)]">Top Affiliates</h2>
        <div className="mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-[var(--bg-secondary)]">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">
                  Affiliate
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                  Clicks
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                  Conversions
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                  Conv. Rate
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">
                  Commissions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
              {loadingPerformance ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-[var(--text-muted)]">
                    Loading...
                  </td>
                </tr>
              ) : !performance || performance.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-[var(--text-muted)]">
                    No data available
                  </td>
                </tr>
              ) : (
                performance.slice(0, 10).map((aff: any) => (
                  <tr key={aff.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                      <div className="font-medium text-[var(--text-primary)]">{aff.name}</div>
                      <div className="text-[var(--text-muted)] text-xs">{aff.referralCode}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)]">
                      {aff.clicks.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)]">
                      {aff.conversions}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-[var(--text-primary)]">
                      {aff.conversionRate}%
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-green-600">
                      ${aff.commissions.toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Traffic Sources */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-[var(--text-primary)]">Traffic Sources</h2>
        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* By Source */}
          <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
            <h3 className="text-sm font-medium text-[var(--text-secondary)]">By Source</h3>
            {loadingTraffic ? (
              <p className="mt-4 text-[var(--text-muted)]">Loading...</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {trafficSources?.bySource?.slice(0, 5).map((source: any) => (
                  <li key={source.name} className="flex justify-between items-center">
                    <span className="text-sm text-[var(--text-secondary)]">{source.name}</span>
                    <div className="text-right">
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {source.clicks} clicks
                      </span>
                      <span className="ml-2 text-xs text-[var(--text-muted)]">
                        ({source.conversionRate}%)
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* By Campaign */}
          <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
            <h3 className="text-sm font-medium text-[var(--text-secondary)]">By Campaign</h3>
            {loadingTraffic ? (
              <p className="mt-4 text-[var(--text-muted)]">Loading...</p>
            ) : (
              <ul className="mt-4 space-y-3">
                {trafficSources?.byCampaign?.slice(0, 5).map((campaign: any) => (
                  <li key={campaign.name} className="flex justify-between items-center">
                    <span className="text-sm text-[var(--text-secondary)] truncate max-w-[200px]">
                      {campaign.name}
                    </span>
                    <div className="text-right">
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {campaign.clicks} clicks
                      </span>
                      <span className="ml-2 text-xs text-[var(--text-muted)]">
                        ({campaign.conversionRate}%)
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="mt-8 flex gap-4">
        <a
          href={`/api/v1/reports/export/clicks?startDate=${startDate}&endDate=${endDate}&format=csv`}
          className="btn btn-secondary"
          download
        >
          Export Clicks (CSV)
        </a>
        <a
          href={`/api/v1/reports/export/commissions?startDate=${startDate}&endDate=${endDate}&format=csv`}
          className="btn btn-secondary"
          download
        >
          Export Commissions (CSV)
        </a>
      </div>
    </div>
  );
}
